import type { TAsyncState, TStateCreator } from "../types";
import {
    initialAsyncState,
    pendingAsyncState,
    fulfilledAsyncState,
    rejectedAsyncState,
} from "../types";

/**
 * Supporting file entity
 */
export type TSupportingFile = {
    id: string;
    dealId: string;
    dealName: string | null;
    period: string | null;
    financialFileId: string | null;
    financialFileName: string | null;
    friendlyName: string;
    originalFilename: string;
    fileSizeBytes: number;
    contentType: string;
    contentHash: string;
    notes: string | null;
    uploadedById: string | null;
    uploadedByName: string | null;
    createdAt: string;
    updatedAt: string;
    rowVersion: string | null;
    canEdit: boolean;
};

/**
 * Supporting file list filters
 */
export type TSupportingFileFilters = {
    dealId?: string;
    financialFileId?: string;
    page?: number;
    pageSize?: number;
};

/**
 * Paginated supporting files response
 */
export type TPaginatedSupportingFiles = {
    items: TSupportingFile[];
    total: number;
    totalUnfiltered?: number;
    page: number;
    pageSize: number;
};

/**
 * Supporting files slice state
 */
export type TSupportingFilesState = {
    supportingFilesListState: TAsyncState<TPaginatedSupportingFiles>;
    supportingFileUploadState: TAsyncState<TSupportingFile>;
    supportingFileFilters: TSupportingFileFilters;
};

/**
 * Supporting files slice actions
 */
export type TSupportingFilesActions = {
    // List operations
    setSupportingFilesLoading: () => void;
    setSupportingFilesList: (data: TPaginatedSupportingFiles) => void;
    setSupportingFilesError: (error: Error) => void;
    setSupportingFileFilters: (filters: Partial<TSupportingFileFilters>) => void;
    resetSupportingFileFilters: () => void;

    // Upload operations
    setSupportingFileUploadLoading: () => void;
    setSupportingFileUploadSuccess: (file: TSupportingFile) => void;
    setSupportingFileUploadError: (error: Error) => void;
    resetSupportingFileUpload: () => void;
};

/**
 * Complete supporting files slice type
 */
export type TSliceSupportingFiles = TSupportingFilesState & TSupportingFilesActions;

const defaultFilters: TSupportingFileFilters = {
    page: 1,
    pageSize: 50,
};

const initialState: TSupportingFilesState = {
    supportingFilesListState: initialAsyncState(),
    supportingFileUploadState: initialAsyncState(),
    supportingFileFilters: defaultFilters,
};

/**
 * Creates the supporting files slice for the store.
 */
export const createSliceSupportingFiles = (): TStateCreator<TSliceSupportingFiles> => {
    return (set) => ({
        ...initialState,

        // List operations
        setSupportingFilesLoading: () => {
            set((state) => ({
                ...state,
                supportingFilesListState: pendingAsyncState(state.supportingFilesListState.data),
            }));
        },

        setSupportingFilesList: (data: TPaginatedSupportingFiles) => {
            set({ supportingFilesListState: fulfilledAsyncState(data) });
        },

        setSupportingFilesError: (error: Error) => {
            set((state) => ({
                ...state,
                supportingFilesListState: rejectedAsyncState(
                    error,
                    state.supportingFilesListState.data,
                ),
            }));
        },

        setSupportingFileFilters: (filters: Partial<TSupportingFileFilters>) => {
            set((state) => ({
                ...state,
                supportingFileFilters: {
                    ...state.supportingFileFilters,
                    ...filters,
                },
            }));
        },

        resetSupportingFileFilters: () => {
            set((state) => ({
                ...state,
                supportingFileFilters: defaultFilters,
            }));
        },

        // Upload operations
        setSupportingFileUploadLoading: () => {
            set((state) => ({
                ...state,
                supportingFileUploadState: pendingAsyncState(null),
            }));
        },

        setSupportingFileUploadSuccess: (file: TSupportingFile) => {
            set({ supportingFileUploadState: fulfilledAsyncState(file) });
        },

        setSupportingFileUploadError: (error: Error) => {
            set((state) => ({
                ...state,
                supportingFileUploadState: rejectedAsyncState(error, null),
            }));
        },

        resetSupportingFileUpload: () => {
            set((state) => ({
                ...state,
                supportingFileUploadState: initialAsyncState(),
            }));
        },
    });
};
